import { Helmet } from "react-helmet-async";

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: string;
  children?: React.ReactNode;
}

const SITE_NAME = "Union Collection";
const DEFAULT_TITLE = "Union Collection | Korean Inspired Minimal Streetwear";
const DEFAULT_DESCRIPTION = "Minimal streetwear influenced by Korean design. No logos, no noise — just clean structure, intentional fits, and limited pieces.";
const DEFAULT_IMAGE = "https://unioncollection.net/og-image.png";
const BASE_URL = "https://unioncollection.net";

const SUPPORTED_LANGS = ["en", "ja", "ko"];

export function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_IMAGE,
  url,
  type = "website",
  children,
}: SEOProps) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE;
  const canonical = url ? `${BASE_URL}${url}` : undefined;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={image} />
      {canonical && <meta property="og:url" content={canonical} />}
      <meta property="og:site_name" content={SITE_NAME} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:site" content="@Collection44937" />

      {/* Canonical */}
      {canonical && <link rel="canonical" href={canonical} />}

      {/* Hreflang tags for multilingual support */}
      {canonical && SUPPORTED_LANGS.map((lang) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={canonical} />
      ))}
      {canonical && <link rel="alternate" hrefLang="x-default" href={canonical} />}

      {children}
    </Helmet>
  );
}

export function OrganizationJsonLd() {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Union Collection",
    alternateName: "Union Collection — Korean Inspired Minimal Streetwear",
    url: "https://unioncollection.net",
    logo: "https://unioncollection.net/favicon.png",
    image: "https://unioncollection.net/og-image.png",
    description:
      "Union Collection is a Korean-inspired minimal streetwear brand. Home of the Union Waistband — our signature internal-belt design. Built on presence, not noise.",
    slogan: "Built on Presence",
    foundingLocation: {
      "@type": "Place",
      name: "Middleton, Wisconsin, United States",
    },
    areaServed: {
      "@type": "Country",
      name: "United States",
    },
    knowsAbout: [
      "Union Waistband",
      "Korean streetwear",
      "Minimal streetwear",
      "Internal belt waistband design",
    ],
    sameAs: [
      "https://www.instagram.com/unioncollection.net",
      "https://x.com/Collection44937",
      "https://www.pinterest.com/unioncollection",
    ],
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer service",
      email: "support@unioncollection.net",
      url: "https://unioncollection.net/contact",
      areaServed: "US",
      availableLanguage: ["English", "Japanese", "Korean"],
    },
  };

  const websiteData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Union Collection",
    url: "https://unioncollection.net",
    potentialAction: {
      "@type": "SearchAction",
      target: "https://unioncollection.net/collection?search={search_term_string}",
      "query-input": "required name=search_term_string",
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      <script type="application/ld+json">{JSON.stringify(websiteData)}</script>
    </Helmet>
  );
}

interface BreadcrumbItem {
  name: string;
  url: string;
}

export function BreadcrumbJsonLd({ items }: { items: BreadcrumbItem[] }) {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: `https://unioncollection.net${item.url}`,
    })),
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
    </Helmet>
  );
}

interface ProductJsonLdProps {
  name: string;
  description?: string;
  image?: string;
  price: number;
  currency?: string;
  availability?: "InStock" | "OutOfStock" | "PreOrder";
  sku?: string;
  rating?: number;
  reviewCount?: number;
  url: string;
}

export function ProductJsonLd({
  name,
  description,
  image,
  price,
  currency = "USD",
  availability = "InStock",
  sku,
  rating,
  reviewCount,
  url,
}: ProductJsonLdProps) {
  const availabilityMap = {
    InStock: "https://schema.org/InStock",
    OutOfStock: "https://schema.org/OutOfStock",
    PreOrder: "https://schema.org/PreOrder",
  };

  const structuredData: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Product",
    name,
    description: description || `${name} from Union Collection`,
    image: image || "https://unioncollection.net/og-image.png",
    url: `https://unioncollection.net${url}`,
    brand: {
      "@type": "Brand",
      name: "Union Collection",
    },
    offers: {
      "@type": "Offer",
      price: (price / 100).toFixed(2),
      priceCurrency: currency,
      availability: availabilityMap[availability],
      seller: {
        "@type": "Organization",
        name: "Union Collection",
      },
    },
  };

  if (sku) {
    structuredData.sku = sku;
  }

  if (rating && reviewCount && reviewCount > 0) {
    structuredData.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: rating.toFixed(1),
      reviewCount,
      bestRating: "5",
      worstRating: "1",
    };
  }

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
    </Helmet>
  );
}
